// src/components/NuevaConsulta.js
import { useState, useEffect, useRef } from 'react';
import LockOutlined from '@mui/icons-material/LockOutlined';
import ConfirmationNumberOutlined from '@mui/icons-material/ConfirmationNumberOutlined';
import VerifiedUserOutlined from '@mui/icons-material/VerifiedUserOutlined';
import api from '../services/api';
// 🚀 NUEVO: Importamos el Hook de autenticación directamente aquí
import { useAuth } from '../context/AuthContext';
import { useClinica } from '../context/ClinicaContext';

import {
    Close as CloseIcon, PersonSearch as PersonSearchIcon, CalendarMonth as CalendarIcon,
    AccessTime as AccessTimeIcon, MedicalServices as MedicalServicesIcon, Search as SearchIcon
} from '@mui/icons-material';
import { euros } from '../utils/formatos';

const DURACIONES = [
    [15, 'Revisión rápida'], [30, 'Cita estándar'], [45, 'Primera visita'],
    [60, 'Valoración completa'], [90, 'Prueba / procedimiento']
];

const HORARIOS_DISPONIBLES = [
    '08:00', '08:30', '09:00', '09:30', '10:00', '10:30',
    '11:00', '11:30', '12:00', '12:30', '13:00', '13:30',
    '16:00', '16:30', '17:00', '17:30', '18:00', '18:30',
    '19:00', '19:30', '20:00'
];

export default function NuevaConsulta({
                                          open, onClose, onSuccess,
                                          pacienteId, horaPredeterminada, profesionalIdPredeterminado
                                      }) {
    // 🚀 Usamos el contexto de forma nativa para no depender de que nos pasen el user desde fuera
    const { user } = useAuth();
    // Especialidad de la cita = la del profesional que la atiende; servicio del tarifario
    const { definiciones, servicios, termino, plantillaDe } = useClinica();
    const [servicio, setServicio] = useState('');
    // Roles que agendan/derivan citas a CUALQUIER médico (no solo a sí mismos).
    // Antes solo 'supermedico' podía elegir médico, así que un admin (o recepción)
    // quedaba forzado a su propio id — que no es un facultativo. Ahora también pueden.
    const rol = user?.role || user?.rol;
    const puedeElegirMedico = ['supermedico', 'admin', 'recepcion'].includes(rol);

    const todayStr = new Date().toISOString().split('T')[0];
    const [fecha, setFecha] = useState(todayStr);
    const [medicos, setMedicos] = useState([]);
    const [medicoSeleccionado, setMedicoSeleccionado] = useState(puedeElegirMedico ? '' : user?.id);

    const [pacientes, setPacientes] = useState([]);
    const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
    const [busqueda, setBusqueda] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);

    const [motivo, setMotivo] = useState('');
    // 🛡️ FIX M6: bandera explícita de atención inmediata (urgencia/walk-in). Antes
    // el bypass del control de solapamiento se activaba escribiendo "URGENCIA" en el
    // motivo; ahora es una decisión intencional y trazable del profesional.
    const [atencionInmediata, setAtencionInmediata] = useState(false);
    // El paciente ya tiene cita a esa hora con otro profesional: se avisa y se
    // puede dar igualmente
    const [avisoPaciente, setAvisoPaciente] = useState('');
    // ⏱️ Duración variable de la cita en minutos (regla de negocio por tipo de acto)
    const [duracion, setDuracion] = useState(30);
    const [horaSeleccionada, setHoraSeleccionada] = useState('');
    const [horasOcupadas, setHorasOcupadas] = useState([]);

    // La ficha y los servicios de la cita son los de la especialidad del profesional
    const idProfesionalCita = puedeElegirMedico ? medicoSeleccionado : user?.id;
    const especialidadProfesional = puedeElegirMedico
        ? medicos.find(m => String(m.id) === String(medicoSeleccionado))?.especialidad
        : user?.especialidad;
    const especialidad = idProfesionalCita ? plantillaDe(especialidadProfesional) : null;
    const serviciosDisponibles = especialidad ? servicios.filter(s => s.activo && s.especialidad === especialidad) : [];

    // 🎟️ Bonos activos del paciente: la sesión se puede descontar de uno
    const [bonosPaciente, setBonosPaciente] = useState([]);
    const [bonoElegido, setBonoElegido] = useState('');
    const bonosAplicables = bonosPaciente.filter(b => b.especialidad === especialidad && (!servicio || b.servicio === servicio));

    // Pólizas vigentes del paciente: la cita puede ir por seguro (la aseguradora
    // paga según su baremo; el paciente, el copago)
    const [polizasPaciente, setPolizasPaciente] = useState([]);
    const [aseguradoras, setAseguradoras] = useState([]);
    const [polizaElegida, setPolizaElegida] = useState('');
    const [autorizacion, setAutorizacion] = useState('');
    const poliza = polizasPaciente.find(p => p.id === polizaElegida);
    const aseguradoraPoliza = poliza && aseguradoras.find(a => a.id === poliza.aseguradora.id);
    const cobertura = aseguradoraPoliza && servicio
        ? aseguradoraPoliza.baremo.find(l => l.servicio === servicio.toUpperCase()) || null
        : undefined; // undefined = aún sin servicio

    // Al cambiar de profesional cambia la especialidad: el servicio y el bono elegidos dejan de valer
    useEffect(() => {
        setServicio('');
        setBonoElegido('');
        setAutorizacion('');
    }, [especialidad]);

    const elegirServicio = (codigo) => {
        setServicio(codigo);
        const bono = bonosPaciente.find(b => b.id === bonoElegido);
        if (bono && bono.servicio !== codigo) setBonoElegido('');
        const s = servicios.find(x => x.codigo === codigo);
        if (s) {
            setDuracion(s.duracion);
            if (!motivo.trim()) setMotivo(s.nombre);
        }
    };

    const elegirBono = (id) => {
        setBonoElegido(id);
        if (id) setPolizaElegida('');
        const bono = bonosPaciente.find(b => b.id === id);
        const s = bono && servicios.find(x => x.codigo === bono.servicio);
        if (bono) setServicio(bono.servicio);
        if (s) {
            setDuracion(s.duracion);
            if (!motivo.trim()) setMotivo(s.nombre);
        }
    };

    const pacienteIdActual = pacienteSeleccionado?.id || pacienteSeleccionado?._id;
    useEffect(() => {
        setBonoElegido('');
        if (!open || !pacienteIdActual) { setBonosPaciente([]); return undefined; }
        let vigente = true;
        api.get(`/bonos?paciente=${pacienteIdActual}`)
            .then(res => { if (vigente) setBonosPaciente((Array.isArray(res) ? res : []).filter(b => b.estado === 'activo')); })
            // Sin permiso o sin bonos: la cita se da igual, sin bono
            .catch(() => { if (vigente) setBonosPaciente([]); });
        return () => { vigente = false; };
    }, [open, pacienteIdActual]);

    useEffect(() => {
        setPolizaElegida('');
        setAutorizacion('');
        if (!open || !pacienteIdActual) { setPolizasPaciente([]); return undefined; }
        let vigente = true;
        Promise.all([api.get(`/seguros/polizas?paciente=${pacienteIdActual}`), api.get('/seguros/aseguradoras')])
            .then(([polizas, asegs]) => {
                if (!vigente) return;
                setPolizasPaciente((Array.isArray(polizas) ? polizas : []).filter(p => p.estado === 'vigente'));
                setAseguradoras(Array.isArray(asegs) ? asegs : []);
            })
            // Sin permiso o sin seguros: la cita se da igual, como privada
            .catch(() => { if (vigente) setPolizasPaciente([]); });
        return () => { vigente = false; };
    }, [open, pacienteIdActual]);

    const elegirPoliza = (id) => {
        setPolizaElegida(id);
        setAutorizacion('');
        if (id) setBonoElegido('');
    };

    const [loadingPacientes, setLoadingPacientes] = useState(false);
    const [loadingHorarios, setLoadingHorarios] = useState(false);
    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (open) {
            if (pacienteId) {
                api.get(`/pacientes/${pacienteId}`)
                    .then(res => {
                        const pac = res.data || res;
                        setPacienteSeleccionado(pac);
                        setBusqueda(`${pac.nombre} ${pac.apellidos} - DNI: ${pac.dni}`);
                    })
                    .catch(err => console.error("Error al pre-cargar paciente:", err));
            }
            if (horaPredeterminada) {
                setHoraSeleccionada(horaPredeterminada);
            }
            if (profesionalIdPredeterminado && puedeElegirMedico) {
                setMedicoSeleccionado(profesionalIdPredeterminado);
            } else if (!puedeElegirMedico && user?.id) {
                setMedicoSeleccionado(user.id); // Médico normal: se asigna a sí mismo
            }
        }
    }, [open, pacienteId, horaPredeterminada, profesionalIdPredeterminado, puedeElegirMedico, user?.id]);

    useEffect(() => {
        if (open && puedeElegirMedico) {
            api.get('/profesionales')
                // Solo quien pasa consulta: sanitarios y administradores con nº de
                // colegiado (la dueña que además atiende). Recepción y gerencia no.
                .then(res => {
                    const lista = Array.isArray(res) ? res : [];
                    setMedicos(lista.filter(m => m.pasaConsulta !== false));
                })
                .catch(err => console.error("Error cargando facultativos:", err));
        }
    }, [open, puedeElegirMedico]);

    useEffect(() => {
        if (!busqueda || busqueda.trim().length < 2 || pacienteSeleccionado) {
            setPacientes([]);
            return;
        }

        const retrasarBusqueda = setTimeout(async () => {
            try {
                setLoadingPacientes(true);
                const res = await api.get(`/pacientes?search=${encodeURIComponent(busqueda)}&limit=5`);
                setPacientes(Array.isArray(res) ? res : []);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingPacientes(false);
            }
        }, 350);

        return () => clearTimeout(retrasarBusqueda);
    }, [busqueda, pacienteSeleccionado]);

    useEffect(() => {
        const targetMedicoId = puedeElegirMedico ? medicoSeleccionado : user?.id;
        if (!fecha || !targetMedicoId) return;

        const consultarOcupacion = async () => {
            try {
                setLoadingHorarios(true);
                const res = await api.get(`/consultas/disponibilidad?fecha=${fecha}&profesional=${targetMedicoId}`);
                setHorasOcupadas(Array.isArray(res) ? res : []);
            } catch (err) {
                console.error("Error consultando disponibilidad:", err);
            } finally {
                setLoadingHorarios(false);
            }
        };
        consultarOcupacion();
    }, [fecha, medicoSeleccionado, puedeElegirMedico, user?.id]);

    useEffect(() => { setAvisoPaciente(''); }, [pacienteSeleccionado, medicoSeleccionado, fecha, horaSeleccionada, duracion]);

    const handleAgendarCita = async (e, confirmarSolapePaciente = false) => {
        e?.preventDefault();
        setErrorMsg('');
        setAvisoPaciente('');

        const targetMedicoId = puedeElegirMedico ? medicoSeleccionado : user?.id;
        if (!pacienteSeleccionado || !targetMedicoId || !horaSeleccionada) {
            return setErrorMsg('Por favor, verifique todos los parámetros requeridos (Paciente, Especialista y Hora).');
        }

        if (poliza && !servicio) {
            return setErrorMsg('Para citar por seguro elige el servicio: la aseguradora paga según su baremo.');
        }
        if (poliza && cobertura === null) {
            return setErrorMsg(`${poliza.aseguradora.nombre} no cubre este servicio. Elige otro o da la cita como privada.`);
        }

        setLoadingSubmit(true);

        const [horas, minutos] = horaSeleccionada.split(':');
        const fechaFinalCita = new Date(fecha);
        fechaFinalCita.setHours(parseInt(horas), parseInt(minutos), 0, 0);

        const ahoraConGracia = new Date();
        ahoraConGracia.setMinutes(ahoraConGracia.getMinutes() - 15);

        if (fechaFinalCita < ahoraConGracia) {
            setLoadingSubmit(false);
            return setErrorMsg('Inconsistencia lógica: El horario seleccionado pertenece al pasado.');
        }

        try {
            await api.post('/consultas', {
                paciente: pacienteSeleccionado.id || pacienteSeleccionado._id,
                profesional: targetMedicoId,
                fecha: fechaFinalCita.toISOString(),
                duracion: duracion,
                motivo: motivo,
                ...(servicio ? { servicio } : {}),
                ...(bonoElegido ? { bono: bonoElegido } : {}),
                ...(poliza ? { seguro: { poliza: poliza.id, autorizacion: autorizacion.trim() } } : {}),
                atencionInmediata, // 🛡️ FIX M6: bypass explícito de solapamiento
                ...(confirmarSolapePaciente ? { confirmarSolapePaciente: true } : {})
            });

            if (onSuccess) onSuccess();
            resetForm();
            onClose();
        } catch (err) {
            if (err.codigo === 'PACIENTE_CON_CITA') setAvisoPaciente(err.message);
            else setErrorMsg(err.message || 'Error de concurrencia al agendar el turno.');
        } finally {
            setLoadingSubmit(false);
        }
    };

    const resetForm = () => {
        setPacienteSeleccionado(null);
        setBusqueda('');
        setMotivo('');
        setAtencionInmediata(false);
        setAvisoPaciente('');
        setDuracion(30);
        setServicio('');
        setBonoElegido('');
        setPolizaElegida('');
        setAutorizacion('');
        setHoraSeleccionada('');
        setFecha(todayStr);
        setErrorMsg('');
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
            <div className="w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]">

                <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 text-clinica-primary bg-clinica-primary/10 rounded-xl">
                            <MedicalServicesIcon />
                        </div>
                        <h2 className="text-xl font-black text-slate-900">Agendar {termino('consulta').toLowerCase()}</h2>
                    </div>
                    <button onClick={() => { resetForm(); onClose(); }} className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-full transition-colors">
                        <CloseIcon />
                    </button>
                </div>

                <form onSubmit={handleAgendarCita} className="flex-1 overflow-y-auto">
                    <div className="p-6">
                        {errorMsg && <div className="p-4 mb-6 text-sm font-bold text-red-600 bg-red-50 border border-red-200 rounded-xl">{errorMsg}</div>}

                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                            <div className="lg:col-span-7 flex flex-col gap-5 p-5 bg-white border border-slate-200 rounded-2xl shadow-sm">
                                <h3 className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-wide">
                                    <PersonSearchIcon fontSize="small" /> 1. Expediente del Paciente
                                </h3>

                                <div className="relative" ref={dropdownRef}>
                                    <label htmlFor="search-paciente" className="block mb-1.5 text-xs font-bold text-slate-600">Buscar Paciente *</label>
                                    <div className="relative">
                                        <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" fontSize="small" />
                                        <input
                                            id="search-paciente" type="text" placeholder="Introduce DNI, nombre o apellidos..."
                                            value={busqueda}
                                            onChange={(e) => { setPacienteSeleccionado(null); setBusqueda(e.target.value); setShowDropdown(true); }}
                                            onFocus={() => setShowDropdown(true)}
                                            className={`w-full py-2.5 pl-10 pr-4 bg-slate-50 border rounded-xl text-slate-700 font-medium focus:outline-none focus:ring-1 transition-all ${pacienteSeleccionado ? 'border-emerald-400 ring-emerald-400/20 bg-emerald-50/30 text-emerald-900' : 'border-slate-200 focus:border-clinica-primary focus:ring-clinica-primary'}`}
                                        />
                                        {loadingPacientes && (
                                            <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-4 h-4 border-2 border-clinica-primary border-t-transparent rounded-full animate-spin"></div></div>
                                        )}
                                    </div>

                                    {showDropdown && busqueda.length >= 2 && !pacienteSeleccionado && (
                                        <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                                            {pacientes.length === 0 && !loadingPacientes ? (
                                                <div className="p-4 text-center text-sm font-bold text-slate-500">No se encontraron pacientes.</div>
                                            ) : (
                                                pacientes.map(p => (
                                                    <button
                                                        key={p.id} type="button"
                                                        // 🔒 Sin relación clínica: el médico no puede darle la primera cita
                                                        disabled={p.accesoHistorial === false}
                                                        onClick={() => { setPacienteSeleccionado(p); setBusqueda(`${p.nombre} ${p.apellidos} - DNI: ${p.dni}`); setShowDropdown(false); }}
                                                        className="w-full flex flex-col items-start px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-0 transition-colors disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent"
                                                    >
                                                        <span className="font-bold text-slate-900">{p.nombre} {p.apellidos}</span>
                                                        <span className="text-xs font-semibold text-slate-500">DNI: {p.dni} • Tel: {p.telefono || '--'}</span>
                                                        {p.accesoHistorial === false && <span className="inline-flex items-center gap-1 text-[11px] font-bold text-amber-700"><LockOutlined sx={{ fontSize: 13 }} aria-hidden="true" /> La primera cita la da recepción</span>}
                                                    </button>
                                                ))
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="flex-1">
                                    {especialidad && (
                                        <p className="mb-3 text-xs font-semibold text-slate-500">
                                            Ficha: <span className="font-black text-slate-700">{definiciones[especialidad]?.nombre || 'Medicina general'}</span> (la especialidad del profesional)
                                        </p>
                                    )}

                                    {serviciosDisponibles.length > 0 && (
                                        <>
                                            <label htmlFor="servicio-cita" className="block mb-1.5 text-xs font-bold text-slate-600">Servicio</label>
                                            <select id="servicio-cita" value={servicio} onChange={(e) => elegirServicio(e.target.value)} className="w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:border-clinica-primary focus:ring-1 focus:ring-clinica-primary transition-all mb-4">
                                                <option value="">— Sin servicio del tarifario —</option>
                                                {serviciosDisponibles.map(s => (
                                                    <option key={s.codigo} value={s.codigo}>{s.nombre} · {s.duracion} min · {euros(Number(s.precio))}</option>
                                                ))}
                                            </select>
                                        </>
                                    )}

                                    {bonosAplicables.length > 0 && (
                                        <>
                                            <label htmlFor="bono-cita" className="flex items-center gap-1.5 mb-1.5 text-xs font-bold text-emerald-700"><ConfirmationNumberOutlined sx={{ fontSize: 16 }} aria-hidden="true" /> Pagar con bono</label>
                                            <select id="bono-cita" value={bonoElegido} onChange={(e) => elegirBono(e.target.value)} className="w-full py-2.5 px-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-900 font-bold focus:outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all mb-4">
                                                <option value="">No usar bono (se cobra aparte)</option>
                                                {bonosAplicables.map(b => (
                                                    <option key={b.id} value={b.id}>{b.nombre} · quedan {b.restantes} de {b.sesiones}</option>
                                                ))}
                                            </select>
                                        </>
                                    )}

                                    {polizasPaciente.length > 0 && (
                                        <div className="mb-4">
                                            <label htmlFor="seguro-cita" className="flex items-center gap-1.5 mb-1.5 text-xs font-bold text-sky-700"><VerifiedUserOutlined sx={{ fontSize: 16 }} aria-hidden="true" /> Por seguro</label>
                                            <select id="seguro-cita" value={polizaElegida} onChange={(e) => elegirPoliza(e.target.value)} className="w-full py-2.5 px-4 bg-sky-50 border border-sky-200 rounded-xl text-sky-900 font-bold focus:outline-none focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-all">
                                                <option value="">No, cita privada</option>
                                                {polizasPaciente.map(p => (
                                                    <option key={p.id} value={p.id}>{p.aseguradora.nombre} · {p.numeroPoliza}</option>
                                                ))}
                                            </select>
                                            {poliza && cobertura === undefined && (
                                                <p className="mt-1.5 text-xs font-semibold text-slate-500">Elige el servicio para ver lo que cubre {poliza.aseguradora.nombre}.</p>
                                            )}
                                            {poliza && cobertura === null && (
                                                <p role="alert" className="mt-1.5 text-xs font-bold text-rose-600">{poliza.aseguradora.nombre} no cubre este servicio en vuestro baremo.</p>
                                            )}
                                            {poliza && cobertura && (
                                                <div className="mt-2 p-3 rounded-xl bg-sky-50 border border-sky-100 text-xs font-semibold text-sky-900 space-y-2">
                                                    <p>
                                                        {poliza.aseguradora.nombre} paga <strong>{euros(Number(cobertura.importe))}</strong>
                                                        {cobertura.copago > 0
                                                            ? <> · el paciente paga un copago de <strong>{euros(Number(cobertura.copago))}</strong></>
                                                            : ' · sin copago para el paciente'}
                                                    </p>
                                                    {cobertura.requiereAutorizacion && (
                                                        <div>
                                                            <label htmlFor="autorizacion-cita" className="block mb-1 font-bold text-amber-700">Necesita autorización de la aseguradora</label>
                                                            <input id="autorizacion-cita" value={autorizacion} onChange={(e) => setAutorizacion(e.target.value)} maxLength={40}
                                                                   placeholder="Nº de autorización (se puede añadir después)" className="w-full py-2 px-3 bg-white border border-amber-200 rounded-lg text-slate-800 font-semibold focus:outline-none focus:border-amber-500" />
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <label htmlFor="duracion-cita" className="block mb-1.5 text-xs font-bold text-slate-600">Duración de la Cita *</label>
                                    <select
                                        id="duracion-cita"
                                        value={duracion}
                                        onChange={(e) => setDuracion(parseInt(e.target.value, 10))}
                                        className="w-full mb-4 py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:border-clinica-primary focus:ring-1 focus:ring-clinica-primary transition-all"
                                    >
                                        {DURACIONES.map(([min, texto]) => <option key={min} value={min}>{min} min — {texto}</option>)}
                                        {!DURACIONES.some(([min]) => min === duracion) && <option value={duracion}>{duracion} min — Según servicio</option>}
                                    </select>

                                    <label htmlFor="motivo-cita" className="block mb-1.5 text-xs font-bold text-slate-600">Motivo Clínico de la Visita *</label>
                                    <textarea id="motivo-cita" required rows="4" value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej. Revisión anual..." className="w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium focus:outline-none focus:border-clinica-primary focus:ring-1 focus:ring-clinica-primary transition-all resize-none" />

                                    {/* 🛡️ FIX M6: bypass explícito del control de solapamiento (urgencias / walk-in) */}
                                    <label htmlFor="atencion-inmediata" className="flex items-start gap-2 mt-3 cursor-pointer select-none">
                                        <input id="atencion-inmediata" type="checkbox" checked={atencionInmediata} onChange={(e) => setAtencionInmediata(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-clinica-primary focus:ring-clinica-primary" />
                                        <span className="text-xs font-medium text-slate-600">Atención inmediata (urgencia / sin cita): permite agendar aunque se solape con otra cita del profesional.</span>
                                    </label>
                                </div>
                            </div>

                            <div className="lg:col-span-5 flex flex-col gap-5 p-5 bg-white border border-slate-200 rounded-2xl shadow-sm">
                                <h3 className="flex items-center gap-2 text-sm font-bold text-slate-500 uppercase tracking-wide">
                                    <CalendarIcon fontSize="small" /> 2. Planificación
                                </h3>

                                {puedeElegirMedico && (
                                    <div>
                                        <label htmlFor="select-medico" className="block mb-1.5 text-xs font-bold text-slate-600">Derivar a {termino('profesional').toLowerCase()} *</label>
                                        <select id="select-medico" required value={medicoSeleccionado} onChange={(e) => { setMedicoSeleccionado(e.target.value); setHoraSeleccionada(''); }} className="w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium cursor-pointer">
                                            <option value="" disabled>Selecciona un profesional…</option>
                                            {medicos.map(m => (
                                                <option key={m.id} value={m.id}>{m.nombre} {m.apellidos} ({m.especialidad || 'General'})</option>
                                            ))}
                                        </select>
                                        {medicoSeleccionado && (
                                            <p className="text-[11px] font-bold text-slate-400 mt-1.5 ml-1">
                                                Los horarios de abajo muestran la disponibilidad de este profesional.
                                            </p>
                                        )}
                                    </div>
                                )}

                                <div>
                                    <label htmlFor="fecha-cita" className="block mb-1.5 text-xs font-bold text-slate-600">Fecha del Turno *</label>
                                    <input
                                        id="fecha-cita"
                                        type="date"
                                        required
                                        min={todayStr}
                                        value={fecha}
                                        onChange={(e) => { setFecha(e.target.value); setHoraSeleccionada(''); }}
                                        className="w-full py-2.5 px-4 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-medium cursor-pointer"
                                    />
                                </div>
                                <div className="flex-1 flex flex-col">
                                    <div className="flex items-center gap-1.5 mb-2 text-xs font-bold text-slate-600 uppercase">
                                        <AccessTimeIcon fontSize="small" /> Hora Disponible *
                                    </div>

                                    {loadingHorarios ? (
                                        <div className="flex justify-center items-center flex-1 py-4"><div className="w-6 h-6 border-2 border-clinica-primary border-t-transparent rounded-full animate-spin"></div></div>
                                    ) : (
                                        <div className="grid grid-cols-3 gap-2 overflow-y-auto pr-1 max-h-[160px] hide-scrollbar" role="group" aria-label="Horarios disponibles">
                                            {HORARIOS_DISPONIBLES.map((slot) => {
                                                const ocupado = horasOcupadas.includes(slot);
                                                const seleccionado = horaSeleccionada === slot;
                                                return (
                                                    <button
                                                        key={slot} type="button" disabled={ocupado} onClick={() => setHoraSeleccionada(slot)}
                                                        className={`py-2 text-xs font-bold rounded-lg transition-all border ${ocupado ? 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed' : seleccionado ? 'bg-clinica-primary text-white border-clinica-primary shadow-md' : 'bg-white text-slate-700 border-slate-200 hover:border-clinica-primary hover:text-clinica-primary'}`}
                                                    >
                                                        {slot}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50 sticky bottom-0">
                        {avisoPaciente && (
                            <div role="alert" className="w-full flex flex-col sm:flex-row sm:items-center gap-3 p-4 text-sm font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl">
                                <span className="flex-1">{avisoPaciente} ¿Quieres darle esta cita igualmente?</span>
                                <div className="flex gap-2 shrink-0">
                                    <button type="button" onClick={() => setAvisoPaciente('')} className="px-4 py-2 text-xs font-bold text-amber-800 bg-white border border-amber-300 hover:bg-amber-100 rounded-lg">Elegir otra hora</button>
                                    <button type="button" disabled={loadingSubmit} onClick={() => handleAgendarCita(null, true)} className="px-4 py-2 text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 rounded-lg disabled:opacity-50">Dar la cita igualmente</button>
                                </div>
                            </div>
                        )}
                        <button type="button" onClick={() => { resetForm(); onClose(); }} className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-100 rounded-xl transition-colors">Cancelar</button>
                        <button type="submit" disabled={loadingSubmit || !horaSeleccionada || !pacienteSeleccionado} className="inline-flex items-center justify-center min-w-[180px] px-5 py-2.5 text-sm font-bold text-white bg-clinica-primary hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 shadow-md">
                            {loadingSubmit ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Confirmar y Agendar Cita'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}